import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import {
  BookOpen,
  PlayCircle,
  FileText,
  ChevronRight
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CoachTraining = ({ hasAccess }) => {
  const [modules, setModules] = useState([]);
  const [selectedModule, setSelectedModule] = useState(null);
  const [lessons, setLessons] = useState([]);

  const fetchModules = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/training/modules`, { withCredentials: true });
      setModules(response.data);
    } catch (err) {
      console.error("Failed to fetch training modules:", err);
      if (err.response?.status === 403) {
        toast.error("Accès à la formation désactivé");
      }
    }
  }, []);

  useEffect(() => {
    if (hasAccess) {
      fetchModules();
    }
  }, [hasAccess, fetchModules]);

  const fetchLessons = async (moduleId) => {
    try {
      const response = await axios.get(`${API}/training/modules/${moduleId}/lessons`, { withCredentials: true });
      setLessons(response.data);
      setSelectedModule(modules.find(m => m.id === moduleId));
    } catch (err) {
      console.error(`Failed to fetch lessons for module ${moduleId}:`, err);
    }
  };

  if (!hasAccess) {
    return (
      <div className="bg-[#0F0F16] border border-white/10 p-12 text-center">
        <BookOpen className="w-12 h-12 text-red-500/30 mx-auto mb-4" />
        <p className="text-[#94A3B8]">
          Votre accès à la formation est actuellement désactivé.
        </p>
        <p className="text-[#64748B] text-sm mt-2">
          Contactez l&apos;administrateur pour plus d&apos;informations.
        </p>
      </div>
    );
  }

  if (selectedModule) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <button
          onClick={() => { setSelectedModule(null); setLessons([]); }}
          className="flex items-center gap-2 text-[#94A3B8] hover:text-white mb-6 transition-colors"
        >
          ← Retour aux modules
        </button>

        <h3 className="font-['Outfit'] text-2xl font-bold mb-2">
          {selectedModule.title}
        </h3>
        <p className="text-[#94A3B8] mb-8">{selectedModule.description}</p>

        {lessons.length === 0 ? (
          <div className="bg-[#0F0F16] border border-white/10 p-12 text-center">
            <p className="text-[#94A3B8]">
              Aucune leçon dans ce module pour le moment.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {lessons.map((lesson, index) => (
              <motion.div
                key={lesson.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-[#0F0F16] border border-white/10 p-6"
                data-testid={`lesson-card-${lesson.id}`}
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-[#0066FF]/10 flex items-center justify-center">
                    <span className="text-[#0066FF] font-bold">{index + 1}</span>
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-lg">{lesson.title}</h4>
                    <p className="text-[#94A3B8] text-sm mt-2">{lesson.description}</p>

                    {/* Video */}
                    {lesson.video_url && (
                      <div className="mt-4">
                        <a
                          href={lesson.video_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 text-[#0066FF] hover:text-[#3B82F6] transition-colors"
                        >
                          <PlayCircle className="w-5 h-5" />
                          Voir la vidéo
                        </a>
                      </div>
                    )}

                    {/* Documents */}
                    {lesson.documents && lesson.documents.length > 0 && (
                      <div className="mt-4 pt-4 border-t border-white/10">
                        <p className="text-[#64748B] text-xs uppercase tracking-wider mb-2">
                          Documents à télécharger
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {lesson.documents.map((doc) => (
                            <a
                              key={`${doc.name}-${doc.url}`}
                              href={doc.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-2 px-3 py-2 bg-[#05050A] border border-white/10 text-sm hover:border-[#0066FF] transition-colors"
                            >
                              <FileText className="w-4 h-4 text-[#0066FF]" />
                              {doc.name}
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
    >
      <h3 className="font-['Outfit'] text-2xl font-bold mb-6">
        Modules de Formation
      </h3>

      {modules.length === 0 ? (
        <div className="bg-[#0F0F16] border border-white/10 p-12 text-center">
          <BookOpen className="w-12 h-12 text-[#0066FF]/30 mx-auto mb-4" />
          <p className="text-[#94A3B8]">
            Aucun module de formation disponible pour le moment.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {modules.map((module, index) => (
            <motion.div
              key={module.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              onClick={() => fetchLessons(module.id)}
              className="bg-[#0F0F16] border border-white/10 p-6 cursor-pointer hover:border-[#0066FF]/50 transition-all group"
              data-testid={`module-card-${module.id}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <span className="text-[#0066FF] text-xs font-bold uppercase tracking-wider">
                    Module {index + 1}
                  </span>
                  <h4 className="font-['Outfit'] text-lg font-bold mt-2 group-hover:text-[#0066FF] transition-colors">
                    {module.title}
                  </h4>
                  <p className="text-[#94A3B8] text-sm mt-2 line-clamp-2">
                    {module.description}
                  </p>
                  <p className="text-[#64748B] text-xs mt-3">
                    {module.lessons_count} leçon{module.lessons_count > 1 ? 's' : ''}
                  </p>
                </div>
                <ChevronRight className="w-5 h-5 text-[#94A3B8] group-hover:text-[#0066FF] transition-colors" />
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </motion.div>
  );
};

export default CoachTraining;
